import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import {
  Badge,
  Button,
  Field,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import { useAccountActionMutation, type AccountAction } from '../api';
import { ROLE_LABEL_KEY, type PlatformAccount } from '../types';

import { ConfirmPasswordField } from '@/components/ui/ConfirmPasswordField';
import { useToast } from '@/components/ui/toast/ToastProvider';
import { ResetAdminPasswordDialog } from '@/features/account-access/components/ResetAdminPasswordDialog';
import { useSession } from '@/lib/session';

type Dialog =
  | { readonly kind: 'closed' }
  | { readonly kind: 'action'; readonly account: PlatformAccount; readonly action: AccountAction };

const CLOSED: Dialog = { kind: 'closed' };

function isLocked(account: PlatformAccount): boolean {
  return account.lockedUntil !== null && new Date(account.lockedUntil).getTime() > Date.now();
}

/**
 * One row per platform account. The buttons offered are a UX hint only: the API and, beneath it, the database functions decide what is
 * legal - never one's own account, never the last superadmin. Every action asks for a reason AND the superadmin's own password again.
 */
export function AccountsTable({
  accounts,
}: {
  accounts: readonly PlatformAccount[];
}): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const { userId } = useSession();
  const mutation = useAccountActionMutation();

  const [dialog, setDialog] = useState<Dialog>(CLOSED);
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [resetTarget, setResetTarget] = useState<{ userId: string; label: string } | null>(null);

  function open(next: Dialog): void {
    setFormError(null);
    setReason('');
    setPassword('');
    setDialog(next);
  }

  function close(): void {
    setReason('');
    setPassword('');
    setDialog(CLOSED);
  }

  async function onConfirm(): Promise<void> {
    if (dialog.kind !== 'action') return;
    setFormError(null);
    try {
      await mutation.mutateAsync({
        userId: dialog.account.userId,
        action: dialog.action,
        reason,
        confirmPassword: password,
      });
      toast.success(t('admin.accounts.updatedToast'));
      close();
    } catch (error) {
      setFormError(t(resolveErrorMessageKey(error)));
      setPassword('');
    }
  }

  const canSubmit = reason.trim().length >= 10 && password.length > 0;
  const action = dialog.kind === 'action' ? dialog.action : null;
  const title =
    action === 'revoke-role'
      ? t('admin.accounts.revokeTitle')
      : action === 'disable'
        ? t('admin.accounts.disableTitle')
        : t('admin.accounts.enableTitle');
  const warning =
    action === 'revoke-role'
      ? t('admin.accounts.revokeWarning')
      : action === 'disable'
        ? t('admin.accounts.disableWarning')
        : null;

  return (
    <>
      <Table caption={t('admin.accounts.table.caption')}>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{t('admin.accounts.table.colAccount')}</TableHeaderCell>
            <TableHeaderCell>{t('admin.accounts.table.colRole')}</TableHeaderCell>
            <TableHeaderCell>{t('admin.accounts.table.colStatus')}</TableHeaderCell>
            <TableHeaderCell>{t('admin.accounts.table.colLastLogin')}</TableHeaderCell>
            <TableHeaderCell> </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {accounts.map((account) => {
            const isSelf = account.userId === userId;
            return (
              <TableRow key={account.userId}>
                <TableCell>
                  <span className="text-sm text-ink">{account.displayName ?? account.email}</span>
                  {isSelf ? (
                    <span className="ms-2">
                      <Badge tone="info">{t('admin.accounts.you')}</Badge>
                    </span>
                  ) : null}
                  <p className="text-xs text-inkFaint">{account.email}</p>
                </TableCell>
                <TableCell>
                  <Badge tone={account.role === 'superadmin' ? 'accent' : 'neutral'}>
                    {t(ROLE_LABEL_KEY[account.role])}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col items-start gap-1">
                    <Badge tone={account.status === 'active' ? 'good' : 'critical'}>
                      {t(
                        account.status === 'active'
                          ? 'admin.accounts.status.active'
                          : 'admin.accounts.status.disabled',
                      )}
                    </Badge>
                    {isLocked(account) ? (
                      <Badge tone="warn">{t('admin.accounts.status.locked')}</Badge>
                    ) : null}
                    {account.mustChangePassword ? (
                      <span className="text-xs text-inkFaint">
                        {t('admin.accounts.provisionalPending')}
                      </span>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-xs text-inkFaint">
                    {account.lastLoginAt === null
                      ? t('admin.accounts.table.neverSignedIn')
                      : new Date(account.lastLoginAt).toLocaleString('fr-TN')}
                  </span>
                </TableCell>
                <TableCell align="end">
                  {isSelf ? null : (
                    <div className="flex flex-wrap justify-end gap-2">
                      {account.role === 'platform_admin' && account.status === 'active' ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setResetTarget({
                              userId: account.userId,
                              label: account.displayName ?? account.email,
                            });
                          }}
                        >
                          {t('admin.access.reset.action')}
                        </Button>
                      ) : null}
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          open({
                            kind: 'action',
                            account,
                            action: account.status === 'active' ? 'disable' : 'enable',
                          });
                        }}
                      >
                        {t(
                          account.status === 'active'
                            ? 'admin.accounts.action.disable'
                            : 'admin.accounts.action.enable',
                        )}
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          open({ kind: 'action', account, action: 'revoke-role' });
                        }}
                      >
                        {t('admin.accounts.action.revoke')}
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <ResetAdminPasswordDialog
        target={resetTarget}
        onClose={() => {
          setResetTarget(null);
        }}
      />

      <Modal
        open={dialog.kind === 'action'}
        title={title}
        onClose={close}
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              {t('merchant.common.cancel')}
            </Button>
            <Button
              onClick={() => {
                void onConfirm();
              }}
              loading={mutation.isPending}
              disabled={!canSubmit}
            >
              {t('merchant.common.confirm')}
            </Button>
          </>
        }
      >
        {formError !== null ? (
          <p
            role="alert"
            className="mb-3 rounded-md bg-criticalSoft px-3 py-2 text-sm text-critical"
          >
            {formError}
          </p>
        ) : null}
        {dialog.kind === 'action' ? (
          <p className="mb-3 text-sm text-ink">
            {dialog.account.displayName ?? dialog.account.email}
          </p>
        ) : null}
        {warning !== null ? (
          <p className="mb-3 rounded-md bg-surface2 px-3 py-2 text-sm text-inkSoft">{warning}</p>
        ) : null}

        <div className="flex flex-col gap-4">
          <Field label={t('admin.common.reasonLabel')}>
            {(bind) => (
              <textarea
                {...bind}
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value);
                }}
                rows={3}
                maxLength={500}
                className="ecom-input"
              />
            )}
          </Field>
          <ConfirmPasswordField value={password} onChange={setPassword} />
        </div>
      </Modal>
    </>
  );
}
